import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import { onboardingLinkBody } from '@pulsepoint/shared';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError } from '../../api/client';
import { useCreateOnboardingLink, useOnboardingLinks, useRevokeOnboardingLink, type OnboardingLink } from '../../api/drivers';
import { useOrganisations } from '../../api/organisations';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { applyApiError } from '../../components/form/apiErrors';
import { FieldRow, FormSection } from '../../components/form/FieldRow';
import { FormSelect } from '../../components/form/FormSelect';
import { FormTextField } from '../../components/form/FormTextField';
import { StatusLabel } from '../../components/StatusLabel';
import { useToast } from '../../components/Toast';
import { formSaver, useLeaveGuard, useUnsavedChanges } from '../../components/unsaved/unsaved';
import { formatDate } from '../../lib/format';
import { colours, radius } from '../../theme';

type In = z.input<typeof onboardingLinkBody>;
type Out = z.output<typeof onboardingLinkBody>;
const DAYS = [
  { value: '7', label: '1 week' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

/**
 * Links drivers open to fill in their own details and cards. Each works for a set time and
 * can be turned off. Sign-ups wait for approval before their cards work.
 */
export function OnboardingLinksDialog({ close }: DialogProps) {
  const phone = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  const orgs = useOrganisations();
  const links = useOnboardingLinks();
  const create = useCreateOnboardingLink();
  const revoke = useRevokeOnboardingLink();
  const dialog = useDialog();
  const toast = useToast();
  const [made, setMade] = useState<OnboardingLink | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const orgOptions = (orgs.data ?? []).map((o) => ({ value: o.id, label: o.businessName }));

  const form = useForm<In, unknown, Out>({
    resolver: zodResolver(onboardingLinkBody),
    values: { organisationId: orgOptions.length === 1 ? orgOptions[0]!.value : '', label: '', days: '30' } as unknown as In,
    resetOptions: { keepDirtyValues: true },
  });

  const submit = formSaver(form, async (v) => {
    setError('');
    setCopied(false);
    try {
      const link = await create.mutateAsync({ ...v, label: v.label ?? undefined });
      setMade(link);
      form.reset();
    } catch (e) {
      setError(applyApiError(e, form.setError, ['organisationId', 'label', 'days']));
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset() });
  const guard = useLeaveGuard();

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast('The link was copied. Paste it into an email or message to your drivers.');
    } catch {
      toast('Copying was blocked. Select the link and copy it yourself.', 'error');
    }
  };

  const live = (links.data ?? []).filter((l) => l.isLive);
  const ended = (links.data ?? []).filter((l) => !l.isLive);

  return (
    <Dialog open onClose={() => guard.close(close)} fullWidth maxWidth="sm" fullScreen={phone} aria-labelledby="links-title">
      <DialogTitle id="links-title">Onboarding links</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 3 }}>
          Send drivers a link to fill in their own details, vehicle and cards. You approve each sign-up before their cards work.
        </Typography>

        <Box
          role="group"
          aria-label="Make a link"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault();
              void submit();
            }
          }}
          sx={{ display: 'grid', gap: 2.5, mb: 3 }}
        >
          {orgOptions.length > 1 && <FormSelect control={form.control} name="organisationId" label="Drivers join" options={orgOptions} />}
          <FieldRow columns="2fr 1fr">
            <FormTextField control={form.control} name="label" label="Name (optional)" placeholder="e.g. Depot staff, October" helperText="Shown on each sign-up, so you know where it came from" />
            <FormSelect control={form.control} name="days" label="Works for" options={DAYS} />
          </FieldRow>
          {error && <Typography color="error" role="alert">{error}</Typography>}
          <Box>
            <Button variant="contained" onClick={() => void submit()} disabled={create.isPending}>
              Make a link
            </Button>
          </Box>
        </Box>

        {made?.url && (
          <Alert severity="success" icon={false} sx={{ mb: 3, display: 'block' }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Your link for {made.organisationName}</Typography>
            <TextField
              value={made.url}
              fullWidth
              size="small"
              slotProps={{
                htmlInput: { readOnly: true, 'aria-label': 'Onboarding link', onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select() },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" onClick={() => void copy(made.url!)} startIcon={copied ? <Check size={16} /> : <Copy size={16} />}>
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ bgcolor: colours.white }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Copy it now: for security, Pulsepoint keeps only a fingerprint of the link and can't show it again. It works until {formatDate(made.expiresAt)}.
            </Typography>
          </Alert>
        )}

        {(live.length > 0 || ended.length > 0) && (
          <FormSection title="Your links">
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px` }}>
              {[...live, ...ended].map((l, i) => (
                <Box component="li" key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 2, py: 1.5, borderTop: i ? `1px solid ${colours.hairline}` : 0 }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontWeight: 500 }}>{l.label ?? 'Unnamed link'}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {[
                        orgOptions.length > 1 ? l.organisationName : null,
                        `${l.submissionCount} sign-up${l.submissionCount === 1 ? '' : 's'}`,
                        l.revokedAt ? `turned off ${formatDate(l.revokedAt)}` : l.isLive ? `works until ${formatDate(l.expiresAt)}` : `ended ${formatDate(l.expiresAt)}`,
                        l.createdBy ? `by ${l.createdBy}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                  {l.isLive ? (
                    <Button
                      size="small"
                      onClick={async () => {
                        const ok = await dialog.confirm({ title: 'Turn this link off?', message: 'Anyone who opens it is told it no longer works. Sign-ups already made stay.', confirmLabel: 'Turn off', danger: true });
                        if (!ok) return;
                        try {
                          await revoke.mutateAsync(l.id);
                          if (made?.id === l.id) setMade(null);
                          toast('The link is turned off.');
                        } catch (e) {
                          toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
                        }
                      }}
                    >
                      Turn off
                    </Button>
                  ) : (
                    <StatusLabel tone="off">Ended</StatusLabel>
                  )}
                </Box>
              ))}
            </Box>
          </FormSection>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => guard.close(close)}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
